import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { JsonEditor } from 'json-edit-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface JSONEditorProps {
  data: Record<string, unknown>
  onUpdate?: (newData: Record<string, unknown>) => void
  loading?: boolean
  error?: string | null
  searchEnabled?: boolean
  restrictDelete?: boolean
  viewOnly?: boolean
  search?: string
}

const JSONEditor = ({
  data,
  onUpdate,
  loading = false,
  error = null,
  searchEnabled = false,
  restrictDelete = true,
  viewOnly = false,
  search,
}: JSONEditorProps) => {
  const { t } = useTranslation()
  const [internalSearchText, setInternalSearchText] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [currentData, setCurrentData] = useState<Record<string, unknown>>(data)
  const [hasChanges, setHasChanges] = useState(false)

  // Merge external search (from parent) with internal search
  const searchText = search || internalSearchText

  useEffect(() => {
    setCurrentData(data)
    setHasChanges(false)
  }, [data])

  useEffect(() => {
    const isDifferent = JSON.stringify(currentData) !== JSON.stringify(data)
    setHasChanges(isDifferent)
  }, [currentData, data])

  const handleUpdate = (newData: unknown) => {
    setCurrentData(newData as Record<string, unknown>)
  }

  const handleSave = () => {
    if (onUpdate && hasChanges) {
      onUpdate(currentData)
    }
  }

  const handleReset = () => {
    setCurrentData(data)
    setHasChanges(false)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">{t('common.noDataAvailable')}</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {t('common.extractedData')}
        </Typography>

        {searchEnabled && (
          <TextField
            size="small"
            placeholder={t('common.search')}
            value={search || internalSearchText}
            onChange={(e) => setInternalSearchText(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              width: isFocused ? 200 : 150,
              transition: 'width 0.3s ease',
            }}
          />
        )}
      </Stack>

      <Box
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          '& .jer-editor-container': {
            width: '100%',
            minWidth: '100%',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: '14px',
            paddingTop: 0,
          },
          '& textarea': {
            maxWidth: '100% !important',
            maxHeight: 'none !important',
          },
        }}
      >
        <JsonEditor
          data={currentData}
          rootName="extractedData"
          searchText={searchEnabled ? searchText : undefined}
          searchFilter={searchEnabled ? 'all' : undefined}
          searchDebounceTime={300}
          restrictDelete={restrictDelete}
          viewOnly={viewOnly}
          setData={viewOnly ? undefined : handleUpdate}
        />
      </Box>

      {!viewOnly && (
        <Stack spacing={1} sx={{ pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={handleReset} disabled={!hasChanges}>
              {t('common.reset')}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={!hasChanges}>
              {t('common.save')}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}

export default JSONEditor
